import { createAsyncThunk } from "@reduxjs/toolkit";
import { OpenShift, OpenShiftDetail } from "../../../types/model/openShift";
import { phpClient } from "../../../network";
import { PhpResponse, IRejectWithValue } from "../../../types";
import { GetOpenShiftsRequest, ClaimOpenShiftRequest, GetOpenShiftDetailsRequest } from "../../../types/request";

type ClaimOpenShiftErrorData = {
    error_type?: string;
    overlap_details?: Array<{
        open_shift_id?: string;
        shift_date?: string;
        shift_code?: string;
        requested_time?: string;
        existing_shift_time?: string;
        reason?: string;
    }>;
    total_requested?: number;
};

export type OpenShiftStatusCountsResponse = {
    counts: {
        draft: number;
        published: number;
    };
    total: number;
};

// Get all open shifts
export const getOpenShifts = createAsyncThunk<OpenShift[], GetOpenShiftsRequest, IRejectWithValue>(
    "openShift/getOpenShifts",
    async (dateRange, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<OpenShift[]>>({
                url: "/Schedule/open_shift_list",
                data: dateRange,
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

export const getOpenShiftStatusCounts = createAsyncThunk<
    OpenShiftStatusCountsResponse,
    { start_date: string; end_date: string },
    IRejectWithValue
>("openShift/getOpenShiftStatusCounts", async (payload, { rejectWithValue }) => {
    try {
        const response = await phpClient.post<PhpResponse<OpenShiftStatusCountsResponse>>({
            url: "/schedule/open_shift_status_counts",
            data: payload,
        });
        return (
            response.data ?? {
                counts: { draft: 0, published: 0 },
                total: 0,
            }
        );
    } catch (error: any) {
        return rejectWithValue(error.message || "An error occurred");
    }
});

// Get user's open shift requests
export const getMyOpenShiftRequests = createAsyncThunk<OpenShift[], void, IRejectWithValue>(
    "openShift/getMyOpenShiftRequests",
    async (_, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<OpenShift[]>>({
                url: "/Schedule/my_open_shift_request_list",
                data: {},
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Get user's claim shift request history
export const getMyClaimShiftRequestHistory = createAsyncThunk<OpenShift[], void, IRejectWithValue>(
    "openShift/getMyClaimShiftRequestHistory",
    async (_, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<OpenShift[]>>({
                url: "/Schedule/my_claim_shift_request_list",
                data: {},
            });
            return response.data ?? [];
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Cancel open shift request
export const cancelOpenShiftRequest = createAsyncThunk<void, string, IRejectWithValue>(
    "openShift/cancelOpenShiftRequest",
    async (shiftId, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<void>>({
                url: `/Schedule/cancel_my_open_shift/${shiftId}`,
                data: {},
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Manager: cancel an open shift (draft or published only)
export const cancelOpenShift = createAsyncThunk<void, string, IRejectWithValue>(
    "openShift/cancelOpenShift",
    async (openShiftId, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<void>>({
                url: `/Schedule/cancel_open_shift/${openShiftId}`,
                data: {},
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);

// Claim open shifts
export const claimOpenShifts = createAsyncThunk<
    string | undefined,
    ClaimOpenShiftRequest,
    IRejectWithValue
>(
    "openShift/claimOpenShifts",
    async (claimData, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<ClaimOpenShiftErrorData>>({
                url: "/Schedule/claim_open_shift",
                data: {
                    open_shift_ids: claimData.shift_ids,
                    notes: claimData.notes || ""
                },
            });
            if (response?.status === false) {
                const reason = response.data?.overlap_details?.[0]?.reason;
                return rejectWithValue(reason || response.message || "Cannot claim this shift.");
            }
            return response.message;
        } catch (error: any) {
            const payload = error?.response?.data;

            // Backend can return useful conflict details in a non-2xx response.
            const payloadReason =
                payload?.data?.error_type === "time_overlap_conflict"
                    ? payload?.data?.overlap_details?.[0]?.reason
                    : undefined;

            return rejectWithValue(
                payloadReason ||
                    payload?.message ||
                    (typeof payload === "string" ? payload : undefined) ||
                    error.message ||
                    "An error occurred"
            );
        }
    }
);

// Get open shift details by ID
export const getOpenShiftDetails = createAsyncThunk<OpenShiftDetail, GetOpenShiftDetailsRequest, IRejectWithValue>(
    "openShift/getOpenShiftDetails",
    async (request, { rejectWithValue }) => {
        try {
            const response = await phpClient.post<PhpResponse<OpenShiftDetail>>({
                url: `/Schedule/open_shift_details/${request.shift_id}`,
            });
            if (!response.data) {
                return rejectWithValue("No data received");
            }
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || "An error occurred");
        }
    }
);
